import React from "react";
import { isHoliday, isWeekend } from "../../utils/italianHolidays";

const DAY_HEADERS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function getValueDisplay(value) {
  if (value === 1) return "1";
  if (value === 0.5) return "½";
  return "";
}

function cycleValue(current) {
  if (current === null || current === undefined) return 0.5;
  if (current === 0.5) return 1;
  return null;
}

export default function TimesheetCalendar({
  year,
  month,
  entries,
  onEntryChange,
  readOnly = false,
  darkMode = false,
}) {
  const daysInMonth = new Date(year, month, 0).getDate();

  // Giorno della settimana del 1° del mese (0=Dom, 1=Lun, ... 6=Sab)
  // Convertiamo a offset lunedì-based: Lun=0, Mar=1, ... Dom=6
  const firstDayOfWeek = new Date(year, month - 1, 1).getDay();
  const offset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

  const getEntry = (day) => entries.find((e) => e.day === day) || { day, value: null, task: "" };

  const handleDayClick = (day) => {
    if (readOnly) return;
    const entry = getEntry(day);
    const newValue = cycleValue(entry.value);
    onEntryChange(day, newValue, entry.task);
  };

  const handleTaskChange = (day, task) => {
    if (readOnly) return;
    const entry = getEntry(day);
    onEntryChange(day, entry.value, task);
  };

  return (
    <div>
      {/* Header giorni settimana */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAY_HEADERS.map((d) => (
          <div
            key={d}
            className={`text-center text-xs font-medium py-1 ${
              darkMode ? "text-neutral-500" : "text-neutral-400"
            }`}
          >
            {d}
          </div>
        ))}
      </div>

      {/* Griglia giorni */}
      <div className="grid grid-cols-7 gap-1">
        {/* Celle vuote per offset */}
        {Array.from({ length: offset }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}

        {/* Giorni del mese */}
        {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
          const entry = getEntry(day);
          const weekend = isWeekend(day, month, year);
          const holiday = isHoliday(day, month, year);
          const nonWorking = weekend || holiday;

          let bgClass;
          if (holiday) {
            bgClass = darkMode ? "bg-pink-900/20" : "bg-pink-50";
          } else if (weekend) {
            bgClass = darkMode ? "bg-neutral-800/50" : "bg-neutral-100";
          } else {
            bgClass = darkMode ? "bg-neutral-900" : "bg-white";
          }

          const hasValue = entry.value !== null && entry.value !== undefined;
          const valueBg = hasValue
            ? entry.value === 1
              ? darkMode
                ? "bg-emerald-500/20 text-emerald-300"
                : "bg-emerald-100 text-emerald-700"
              : darkMode
                ? "bg-amber-500/20 text-amber-300"
                : "bg-amber-100 text-amber-700"
            : "";

          return (
            <div
              key={day}
              className={`rounded-lg border p-1.5 min-h-[60px] transition-colors ${bgClass} ${
                darkMode ? "border-neutral-800" : "border-neutral-200"
              } ${!readOnly && !nonWorking ? "cursor-pointer hover:ring-1 hover:ring-forfy-300" : ""}`}
              onClick={() => handleDayClick(day)}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`text-xs font-medium ${
                    nonWorking
                      ? darkMode
                        ? "text-neutral-600"
                        : "text-neutral-400"
                      : darkMode
                        ? "text-neutral-400"
                        : "text-neutral-600"
                  }`}
                >
                  {day}
                </span>
                {hasValue && (
                  <span
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${valueBg}`}
                  >
                    {getValueDisplay(entry.value)}
                  </span>
                )}
              </div>
              {!readOnly && (
                <input
                  type="text"
                  value={entry.task || ""}
                  onChange={(e) => handleTaskChange(day, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  placeholder=""
                  className={`w-full mt-1 text-[10px] px-1 py-0.5 rounded border-0 bg-transparent outline-none ${
                    darkMode
                      ? "text-neutral-400 placeholder-neutral-700"
                      : "text-neutral-600 placeholder-neutral-300"
                  }`}
                  maxLength={40}
                />
              )}
              {readOnly && entry.task && (
                <p className={`text-[10px] mt-1 truncate ${darkMode ? "text-neutral-500" : "text-neutral-400"}`}>
                  {entry.task}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
